// Map generation system for Slay the Spire / FTL style branching paths
// Generates a DAG (Directed Acyclic Graph) with non-crossing edges

import type { MapNode, MapEdge, MapState, MapConfig, NodeType, EventType } from "@/types/map.types"
import { DEFAULT_MAP_CONFIG } from "@/types/map.types"
import { getTotalLevels } from "@/data/levels"
import { itemDefinitions, getRandomUniqueItem } from "@/data/item-types"

// Seeded random number generator (Mulberry32)
function createSeededRandom(seed: string): () => number {
  let h = 0
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(31, h) + seed.charCodeAt(i) | 0
  }
  return function() {
    h |= 0
    h = h + 0x6D2B79F5 | 0
    let t = Math.imul(h ^ h >>> 15, 1 | h)
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t
    return ((t ^ t >>> 14) >>> 0) / 4294967296
  }
}

// Generate a unique seed
function generateSeed(): string {
  return `map_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

// Get a random unique item ID for pelf rewards - pelf always gives unique items
function getRandomPelfItemId(_random: () => number): string {
  const uniqueItem = getRandomUniqueItem()
  // Fallback to any item if no unique items available (shouldn't happen)
  if (!uniqueItem) {
    const itemIds = Object.keys(itemDefinitions)
    return itemIds[Math.floor(Math.random() * itemIds.length)]
  }
  return uniqueItem.id
}

// Get level tier based on row position
function getLevelTier(row: number, totalRows: number): number {
  const progress = row / (totalRows - 1)
  if (progress < 0.25) return 0      // Tier 1: levels 0-3 (rat, goblin, mushroom, lizardman)
  if (progress < 0.5) return 1       // Tier 2: levels 4-6 (zombie, orc, ghost)
  if (progress < 0.75) return 2      // Tier 3: levels 7-10 (gnoll, golem, lich, vampire)
  return 3                           // Tier 4: levels 11-13 (ent, beholder, demon)
}

// Get level index pool for a tier
function getLevelPoolForTier(tier: number): number[] {
  const totalLevels = getTotalLevels()
  switch (tier) {
    case 0: return [0, 1, 2, 3].filter(i => i < totalLevels)
    case 1: return [4, 5, 6].filter(i => i < totalLevels)
    case 2: return [7, 8, 9, 10].filter(i => i < totalLevels)
    case 3: return [11, 12, 13].filter(i => i < totalLevels)
    default: return [0]
  }
}

// Calculate node position with slight jitter for organic look
function calculateNodePosition(
  row: number,
  column: number,
  totalRows: number,
  columns: number,
  random: () => number
): { x: number; y: number } {
  const horizontalSpacing = 70
  const verticalSpacing = 90
  const jitterX = (random() - 0.5) * 16
  const jitterY = (random() - 0.5) * 12

  // Calculate base position (centered in viewport width of ~360px for portrait mobile)
  const viewportWidth = 360
  const totalWidth = (columns - 1) * horizontalSpacing
  const startX = (viewportWidth - totalWidth) / 2

  const x = startX + column * horizontalSpacing + jitterX
  // Invert Y so row 0 is at bottom, higher rows are at top
  const y = (totalRows - 1 - row) * verticalSpacing + 60 + jitterY

  return { x, y }
}

// Generate the map structure
export function generateMap(config: MapConfig = DEFAULT_MAP_CONFIG, providedSeed?: string): MapState {
  const seed = providedSeed || generateSeed()
  const random = createSeededRandom(seed)

  // Choose total rows between min and max
  const totalRows = Math.floor(random() * (config.maxRows - config.minRows + 1)) + config.minRows

  const nodes: MapNode[] = []
  const edges: MapEdge[] = []
  const usedLevels: Set<number> = new Set()
  let lastRowHadEvent = false

  // Helper to pick a level index for a tier
  const pickLevelForTier = (tier: number): number => {
    const pool = getLevelPoolForTier(tier)
    // Prefer unused levels first
    const unusedInPool = pool.filter(l => !usedLevels.has(l))
    const candidates = unusedInPool.length > 0 ? unusedInPool : pool
    const level = candidates[Math.floor(random() * candidates.length)]
    usedLevels.add(level)
    return level
  }

  // Generate nodes row by row
  for (let row = 0; row < totalRows; row++) {
    const isFirstRow = row === 0
    const isLastRow = row === totalRows - 1
    const isFirstHalf = row < totalRows / 2

    const nodesInRow: MapNode[] = []

    if (isFirstRow) {
      // First row: single starting node at center
      const columns = [2] // Single centered starting node
      for (const col of columns) {
        const tier = getLevelTier(row, totalRows)
        const levelIndex = pickLevelForTier(tier)
        nodesInRow.push({
          id: `node_${row}_${col}`,
          row,
          column: col,
          type: "regular",
          status: "available", // Starting nodes are available
          levelIndex,
          position: calculateNodePosition(row, col, totalRows, config.columns, random),
        })
      }
    } else if (isLastRow) {
      // Last row: single boss node in the center
      const col = Math.floor(config.columns / 2)
      nodesInRow.push({
        id: `node_${row}_${col}`,
        row,
        column: col,
        type: "boss",
        status: "locked",
        levelIndex: getTotalLevels() - 1, // Final level (demon lord)
        position: calculateNodePosition(row, col, totalRows, config.columns, random),
      })
    } else {
      // Middle rows: 3-4 nodes with type distribution
      const numNodes = random() < 0.5 ? 3 : 4

      // Spread nodes across columns - prefer variety
      const availableColumns = [0, 1, 2, 3, 4]
      const selectedColumns: number[] = []

      for (let i = 0; i < numNodes && availableColumns.length > 0; i++) {
        // Pick a column that maintains connectivity
        const prevRowNodes = nodes.filter(n => n.row === row - 1)
        const validColumns = availableColumns.filter(col => {
          // Must be reachable from at least one node in previous row
          return prevRowNodes.some(prev => Math.abs(prev.column - col) <= 1)
        })

        const candidates = validColumns.length > 0 ? validColumns : availableColumns
        const colIndex = Math.floor(random() * candidates.length)
        const col = candidates[colIndex]
        selectedColumns.push(col)
        availableColumns.splice(availableColumns.indexOf(col), 1)
      }

      selectedColumns.sort((a, b) => a - b)

      for (const col of selectedColumns) {
        // Determine node type
        let type: NodeType = "regular"
        const typeRoll = random()

        if (!isFirstHalf && typeRoll < config.bossPercent) {
          // Boss nodes only in second half
          type = "boss"
        } else if (typeRoll < config.bossPercent + config.eventPercent && !lastRowHadEvent) {
          // Event nodes, but never 2 in a row
          type = "event"
        }

        const tier = getLevelTier(row, totalRows)
        const levelIndex = type === "event" ? null : pickLevelForTier(tier)

        // For event nodes, randomly assign shop (50%) or pelf (50%)
        let eventType: EventType | undefined
        let pelfItemId: string | undefined
        if (type === "event") {
          eventType = random() < 0.5 ? "shop" : "pelf"
          if (eventType === "pelf") {
            pelfItemId = getRandomPelfItemId(random)
          }
        }

        nodesInRow.push({
          id: `node_${row}_${col}`,
          row,
          column: col,
          type,
          status: "locked",
          levelIndex,
          eventType,
          eventRevealed: false,
          pelfItemId,
          position: calculateNodePosition(row, col, totalRows, config.columns, random),
        })
      }

      lastRowHadEvent = nodesInRow.some(n => n.type === "event")
    }

    nodes.push(...nodesInRow)

    // Generate edges from previous row to current row
    if (row > 0) {
      const prevRowNodes = nodes.filter(n => n.row === row - 1)
      const currentRowNodes = nodesInRow

      // Special case: row 1 (second row) - ALL nodes must connect to the first node
      // This ensures all paths are accessible from the start
      if (row === 1) {
        const firstNode = prevRowNodes[0] // First row only has one node
        for (const currNode of currentRowNodes) {
          edges.push({
            fromNodeId: firstNode.id,
            toNodeId: currNode.id,
          })
        }
      } else {
        // Normal connection logic for other rows
        // Each node in prev row connects to 1-2 nodes in current row
        for (const prevNode of prevRowNodes) {
          const reachable = currentRowNodes.filter(
            curr => Math.abs(curr.column - prevNode.column) <= 1
          )

          if (reachable.length === 0) continue

          // Connect to at least 1, possibly 2 nodes
          const numConnections = reachable.length === 1 ? 1 : (random() < 0.6 ? 1 : 2)

          // Shuffle reachable nodes
          const shuffled = [...reachable].sort(() => random() - 0.5)

          for (let i = 0; i < Math.min(numConnections, shuffled.length); i++) {
            edges.push({
              fromNodeId: prevNode.id,
              toNodeId: shuffled[i].id,
            })
          }
        }

        // Ensure every node in current row has at least one incoming edge
        for (const currNode of currentRowNodes) {
          const hasIncoming = edges.some(e => e.toNodeId === currNode.id)
          if (!hasIncoming) {
            // Find a node from prev row that can reach this one
            const canConnect = prevRowNodes.filter(
              prev => Math.abs(prev.column - currNode.column) <= 1
            )
            if (canConnect.length > 0) {
              const source = canConnect[Math.floor(random() * canConnect.length)]
              edges.push({
                fromNodeId: source.id,
                toNodeId: currNode.id,
              })
            }
          }
        }
      }
    }
  }

  // Ensure all paths can reach the final boss
  const finalNode = nodes.find(n => n.row === totalRows - 1)
  if (finalNode) {
    const preBossNodes = nodes.filter(n => n.row === totalRows - 2)
    for (const preBoss of preBossNodes) {
      const hasPathToFinal = edges.some(e => e.fromNodeId === preBoss.id && e.toNodeId === finalNode.id)
      if (!hasPathToFinal && Math.abs(preBoss.column - finalNode.column) <= 1) {
        edges.push({
          fromNodeId: preBoss.id,
          toNodeId: finalNode.id,
        })
      }
    }
  }

  return {
    seed,
    nodes,
    edges,
    currentNodeId: null,
    visitedNodeIds: [],
    totalRows,
  }
}

// Get nodes that are currently available to select
export function getAvailableNodes(map: MapState): MapNode[] {
  // Determine which node to use as the source for connected edges
  // Use currentNodeId if set, otherwise use the last visited node (after completing a level)
  const sourceNodeId = map.currentNodeId || map.visitedNodeIds[map.visitedNodeIds.length - 1]

  // If no source node, return starting nodes (row 0 available nodes)
  if (!sourceNodeId) {
    return map.nodes.filter(n => n.status === "available" && n.row === 0)
  }

  // Only return nodes directly connected from the source node
  const connectedIds = map.edges
    .filter(e => e.fromNodeId === sourceNodeId)
    .map(e => e.toNodeId)

  return map.nodes.filter(n =>
    n.status === "available" && connectedIds.includes(n.id)
  )
}

// Check if a specific node is accessible from current position
export function isNodeAccessible(map: MapState, nodeId: string): boolean {
  const available = getAvailableNodes(map)
  return available.some(n => n.id === nodeId)
}

// Advance player to a new node (called after completing a level)
export function advanceToNode(map: MapState, nodeId: string): MapState {
  const node = map.nodes.find(n => n.id === nodeId)
  if (!node) return map

  // Update node statuses
  const updatedNodes = map.nodes.map(n => {
    if (n.id === nodeId) {
      return { ...n, status: "current" as const }
    }
    if (n.id === map.currentNodeId) {
      return { ...n, status: "completed" as const }
    }
    return n
  })

  // Update available status for connected nodes
  const connectedIds = map.edges
    .filter(e => e.fromNodeId === nodeId)
    .map(e => e.toNodeId)

  const finalNodes = updatedNodes.map(n => {
    if (connectedIds.includes(n.id) && n.status === "locked") {
      return { ...n, status: "available" as const }
    }
    return n
  })

  return {
    ...map,
    currentNodeId: nodeId,
    visitedNodeIds: map.currentNodeId
      ? [...map.visitedNodeIds, map.currentNodeId]
      : map.visitedNodeIds,
    nodes: finalNodes,
  }
}

// Mark current node as completed and update available nodes
export function completeCurrentNode(map: MapState): MapState {
  if (!map.currentNodeId) return map

  const currentNode = map.nodes.find(n => n.id === map.currentNodeId)
  if (!currentNode) return map

  // Mark current node as completed
  const updatedNodes = map.nodes.map(n => {
    if (n.id === map.currentNodeId) {
      return { ...n, status: "completed" as const }
    }
    return n
  })

  // Update available status for connected nodes
  const connectedIds = map.edges
    .filter(e => e.fromNodeId === map.currentNodeId)
    .map(e => e.toNodeId)

  const finalNodes = updatedNodes.map(n => {
    if (connectedIds.includes(n.id) && n.status === "locked") {
      return { ...n, status: "available" as const }
    }
    return n
  })

  return {
    ...map,
    visitedNodeIds: [...map.visitedNodeIds, map.currentNodeId],
    currentNodeId: null, // Clear current, player will select next
    nodes: finalNodes,
  }
}

// Get node by ID
export function getNodeById(map: MapState, nodeId: string): MapNode | undefined {
  return map.nodes.find(n => n.id === nodeId)
}

// Check if the map is complete (boss defeated)
export function isMapComplete(map: MapState): boolean {
  const bossNode = map.nodes.find(n => n.row === map.totalRows - 1 && n.type === "boss")
  return bossNode ? map.visitedNodeIds.includes(bossNode.id) : false
}
